import React, { useEffect, useMemo, useState } from 'react';
import Breadcrumb from '../../components/BreadCrumb/BreadCrumb';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import SearchBar from '../../components/Common/SearchBar/SearchBar';
import { useDispatch, useSelector } from 'react-redux';
import { getProjectDetailsProjectWise } from '../../redux/unitSlice/unitSlice';
import CustomSelect from '../../components/Common/CustomSelect/CustomSelect';
import CustomTable from '../../components/Common/CustomTable/CustomTable';
import CustomPagination from '../../components/Common/CustomPagination/CustomPagination';
import CustomSelectWithFilter from '../../components/Common/CustomSelect/CustomSelectWithFilter';

const ProjectDetailsAdmin = () => {
        const { unitRegionName } = useParams();
        const [searchParams] = useSearchParams();
        const [filterParameter, setFilterParameter] = useState('REVENUE');
        const [currentPage, setCurrentPage] = useState(1);
        const pageSize = 10; // Items per page
        const { projectDetailsProjectWiseData, loading, totalProjectDetailsProjectWiseData } = useSelector((state) => state.unit);
        const [search, setSearch] = useState('');

        const unitType = searchParams.get('unitType')?.toUpperCase(); // automatically decoded
        const mnfUnit = searchParams.get('mnfUnit')?.toUpperCase(); // automatically decoded
        const navigate = useNavigate();
        function fetchUnitRegionNameFromRoute() {
                const formattedUnitRegionName = decodeURIComponent(unitRegionName).replace(/\s+/g, ' ');
                return formattedUnitRegionName;
        }
        const dispatch = useDispatch();

        // Defensive: antd's Table crashes on a non-array dataSource
        // ("rawData.some is not a function"), so a still-loading/error value
        // (e.g. null right after a failed request) is coerced to an array.
        const projectRows = Array.isArray(projectDetailsProjectWiseData) ? projectDetailsProjectWiseData : [];

        const financialYearKeys = useMemo(() => {
                if (!projectRows.length) return [];

                // Find the object with the maximum number of keys
                const referenceObject = projectRows.reduce((maxObj, currentObj) => (Object.keys(currentObj).length > Object.keys(maxObj).length ? currentObj : maxObj));

                // Filter out unwanted keys
                const allKeys = Object.keys(referenceObject);
                return allKeys.filter((key) => !['description', 'upto', 'Total', 'PROJ_NAME', 'IO_NO', 'REV_NO'].includes(key));
        }, [projectRows]);

        const staticProjectSummaryColumns = [
                {
                        title: 'PROJECT NAME',
                        dataIndex: 'PROJ_NAME',
                        key: 'PROJ_NAME',
                        align: 'left', // ✅ Fix applied
                        width: 250,
                        render: (text, record) => (
                                <div
                                        className="text-blue-500 underline cursor-pointer"
                                        onClick={() => {
                                                navigate(`/unit-region/${fetchUnitRegionNameFromRoute()}/${encodeURIComponent(text)}?unitType=${unitType}&mnfUnit=${mnfUnit}&ioNo=${record.IO_NO}&revNo=${record.REV_NO}`);
                                        }}>
                                        {text || '--'}
                                </div>
                        ),
                },
                {
                        title: 'Total',
                        dataIndex: 'Total',
                        key: 'Total',
                        align: 'right',
                        width: 150,

                        render: (text) => <div className="text-right">{text}</div>,
                },
                {
                        title: 'Upto',
                        dataIndex: 'upto',
                        key: 'upto',
                        align: 'right',
                        width: 150,

                        render: (text) => <div className="text-right">{text}</div>,
                },
        ];

        const dynamicProjectSummaryColumn = useMemo(() => {
                return financialYearKeys.map((key) => ({
                        title: key,
                        dataIndex: key,
                        key,
                        align: 'right',
                        width: 150,

                        render: (text) => {
                                return <div className="text-right">{text ?? '-'}</div>;
                        },
                }));
        }, [financialYearKeys]);

        const combinedColumns = useMemo(() => {
                return [...staticProjectSummaryColumns, ...dynamicProjectSummaryColumn];
        }, [dynamicProjectSummaryColumn]);

        const handleFilterChange = (value) => {
                setFilterParameter(value);
                setCurrentPage(1);
        };

        useEffect(() => {
                dispatch(
                        getProjectDetailsProjectWise({
                                mnfUnit,
                                filterParameter,
                                pageSize,
                                page: currentPage,
                                search,
                        }),
                );
        }, [filterParameter, currentPage, mnfUnit, search]);

        return (
                <div className="px-5 flex flex-col gap-6 pt-8 pb-20">
                        <div className="flex flex-col gap-6">
                                <Breadcrumb queryParams={`unitType=${unitType}&mnfUnit=${mnfUnit}`} />
                        </div>
                        <div className=" flex justify-between">
                                <div className="flex gap-3">
                                        <div>
                                                <div className="font-inter font-semibold text-[24px] leading-[32px] tracking-[0%] text-[#101828]">{unitRegionName}</div>
                                        </div>
                                </div>
                        </div>
                        <div className="text-gray-500 text-sm flex justify-start">* All figures are in Rupees (in lakhs).</div>
                        <div className="font-semibold text-[20px] leading-[30px] tracking-[0%]">Project Details</div>
                        <div className="flex justify-between">
                                <SearchBar onSearch={(value) => setSearch(value)} />
                                <div className="flex gap-2">
                                        <CustomSelectWithFilter
                                                placeholder="Revenue"
                                                className="w-[15rem]"
                                                showSearch="true"
                                                allowClear={false}
                                                data={[
                                                        {
                                                                value: 'REVENUE',
                                                                label: 'Revenue',
                                                        },
                                                        {
                                                                value: 'PROJECT_COST',
                                                                label: 'Project Cost',
                                                        },
                                                        {
                                                                value: 'PROFIT_LOSS',
                                                                label: 'Profit/Loss',
                                                        },
                                                        {
                                                                value: 'APPORTIONED_COST',
                                                                label: 'Apportioned Cost',
                                                        },
                                                        {
                                                                value: 'OPERATIONAL_INCOME',
                                                                label: 'Operational Income',
                                                        },
                                                        {
                                                                value: 'PROJECT_PROFIT',
                                                                label: 'Project Profit',
                                                        },

                                                        {
                                                                value: 'NET_BILLING',
                                                                label: 'Net Billing',
                                                        },
                                                        {
                                                                value: 'CASH_INFLOW',
                                                                label: 'Cash Inflow',
                                                        },
                                                        {
                                                                value: 'CASH_OUTFLOW',
                                                                label: 'Cash Outflow',
                                                        },
                                                        {
                                                                value: 'NET_CASH_FLOW',
                                                                label: 'Net Cash Flow',
                                                        },
                                                ]}
                                                onChange={handleFilterChange}
                                        />
                                </div>
                        </div>
                        <div className="text-[18px] leading-[28px] tracking-[0%] font-medium flex flex-col gap-3">
                                <div>{`${filterParameter?.split('_').join(' ')}`}</div>
                                <div>
                                        {' '}
                                        <CustomTable
                                                dataSource={projectRows}
                                                columns={combinedColumns}
                                                loading={loading}
                                        />
                                </div>
                        </div>
                        <div>
                                <CustomPagination
                                        totalItems={totalProjectDetailsProjectWiseData}
                                        pageSize={pageSize}
                                        currentPage={currentPage}
                                        setCurrentPage={setCurrentPage}
                                />
                        </div>
                </div>
        );
};

export default ProjectDetailsAdmin;
